<template>
  <b-row class="p-4">
    <b-col v-if="!inputSelectionField" cols="12">
      <div
        v-for="({ key, name, description, type, options, departments, required }) in fields.filter(({ key }) => !key.startsWith('HIDDEN'))"
        v-if="!key.includes('TALENT') || (key.includes('TALENT') && user.isTalent)"
        :key="key"
        class="mb-5"
      >
        <b-form-group
          v-if="['STRING', 'ENUM', 'TEXTAREA', 'LOCATION', 'CV_UPLOAD'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)"
          :label-for="`input-${key}`"
        >
          <h4><b>{{name}}</b></h4>
          <p class="text-muted">{{description}}</p>
          <b-form-select v-if="type === 'ENUM' && options" :id="`input-${key}`" type="text" :required="required"
            :options="options" :value="inputValues[key]" @input="value => inputValues[key] = value">
              <template v-slot:first><option :value="null">-- Please select an option --</option></template>
          </b-form-select>
          <b-form-input
            v-else-if="type === 'RANGE'"
            :id="`input-${key}`"
            :value="inputValues[key]"
            @input="value => inputValues[key] = value"
            type="range"
            min="0"
            max="200000"
          ></b-form-input>
          <b-form-textarea
            v-if="type === 'TEXTAREA'"
            :id="`input-${key}`"
            type="text"
            :required="required"
            :value="inputValues[key]"
            @input="value => inputValues[key] = value"
            rows="3"
            max-rows="6"
          ></b-form-textarea>
          <div v-else-if="type === 'CV_UPLOAD'">
            <asset-block class="m-0" accept=".txt,.rtf,.pdf,.doc,.docx,.odt" :field="{ type: 'DOCUMENT' }"
              id="formResume" placeholder="Select a resume" v-model="inputValues[key]"/>
          </div>
          <template v-else-if="type === 'LOCATION'">
            <location-search
              :value="inputValues[key] ? inputValues[key].location : null"
              :multiple="inputValues[key] ? Array.isArray(inputValues[key].location) : false"
              @input="value => $set(inputValues, key, {
                remote: inputValues[key] ? inputValues[key].remote : null,
                location: value,
              })"
            />
            <b-form-checkbox
                v-if="!$root.isCXR"
                :checked="inputValues[key] ? inputValues[key].remote : false"
                @input="value => $set(inputValues, key, {
                remote: value,
                location: inputValues[key] ? inputValues[key].location : null,
                })"
                size="lg"
            ><span class="pt-2"> Are you interested in opportunities with remote working?</span></b-form-checkbox>
          </template>
          <b-form-input v-else :id="`input-${key}`" type="text" :required="required"
            :value="inputValues[key]" @input="value => inputValues[key] = value"/>
        </b-form-group>
        <job-title-selector
          v-if="type === 'JOB_TITLES'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :options="options"
          :required="required"
          @input="value => inputValues[key] = value"
          @setJobCategory="setJobCategory($event)"
        />
        <job-skills-selector
          v-if="type === 'JOB_SKILLS' || type === 'JOB_DEVELOPMENT_SKILLS'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :options="options"
          :required="required"
          :isFreeText="!$root.isCXR"
          @input="skillsInput($event, key, type)"
        />
        <languages-selector
          v-if="type === 'LANGUAGES'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :required="required"
          @input="value => inputValues[key] = value"
        />
        <qualifications-editor
          v-if="type === 'QUALIFICATIONS'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :required="required"
          @input="value => inputValues[key] = value"
        />
        <date-editor
          v-if="type === 'DATE'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :required="required"
          @input="value => inputValues[key] = value"
        />
        <job-history-editor
          v-if="type === 'JOB_HISTORY'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :required="required"
          @input="value => inputValues[key] = value"
        />
        <job-business-area-selector
          v-if="type === 'JOB_BUSINESS_AREA'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :options="options"
          :required="required"
          :multiple="true"
          :settings="{ noTitle: true }"
          @input="value => inputValues[key] = value"
        />
        <job-seniority-selector
          v-if="type === 'JOB_SENIORITY'"
          :value="inputValues[key]"
          :name="name"
          :description="description"
          :options="options"
          :required="required"
          :multiple="true"
          :settings="{ noTitle: true }"
          @input="value => inputValues[key] = value"
        />
        <div class="d-flex justify-content-between w-100" v-if="type === 'CHECKBOX'">
          <div>
            <h4><b>{{name}}</b></h4>
            <p class="text-muted">{{description}}</p>
          </div>
          <b-form-checkbox
            :checked="inputValues[key]"
            @input="value => inputValues[key] = value"
            size="lg"
          >
          </b-form-checkbox>
        </div>
      </div>
    </b-col>

    <!-- Legacy job interest blocks -->
    <b-col v-else cols="12">
      <b-card
        v-for="(fieldSelection, index) in inputValues[inputSelectionField]"
        :key="`key-${index}`"
        class="mb-5"
      >
        <div
          v-for="({ key, name, description, type, options, departments, required }) in fields"
          :key="key"
          class="mb-5"
        >
          <b-form-group
            v-if="['STRING', 'ENUM'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)"
            :label="name"
            :label-for="`input-${index}-${key}`"
            :description="description"
          >
            <b-form-select v-if="type === 'ENUM' && options" :id="`input-${index}-${key}`" type="text" :required="required"
              :options="options" :value="getSelectionFieldValue(index, key)"
              @input="value => setSelectionFieldValue(index, key, value)">
                <template v-slot:first><option :value="null">-- Please select an option --</option></template>
            </b-form-select>
            <b-form-input
              v-else-if="type === 'RANGE'"
              :id="`input-${index}-${key}`"
              :value="getSelectionFieldValue(index, key)"
              @input="value => setSelectionFieldValue(index, key, value)"
              type="range"
              min="0"
              max="200000"
            ></b-form-input>
            <b-form-input v-else :id="`input-${index}-${key}`" type="text" :required="required"
              :value="getSelectionFieldValue(index, key)"
              @input="value => setSelectionFieldValue(index, key, value)"/>
          </b-form-group>
          <job-title-selector
            v-if="type === 'JOB_TITLES'"
            :name="name"
            :description="description"
            :options="options"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
            @setJobCategory="setJobCategory($event)"
          />
          <job-skills-selector
            v-if="type === 'JOB_SKILLS'"
            :name="name"
            :description="description"
            :options="options"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
          />
          <languages-selector
            v-if="type === 'LANGUAGES'"
            :name="name"
            :description="description"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
          />
          <qualifications-editor
            v-if="type === 'QUALIFICATIONS'"
            :name="name"
            :description="description"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
          />
          <date-editor
            v-if="type === 'DATE'"
            :name="name"
            :description="description"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
          />
          <job-history-editor
            v-if="type === 'JOB_HISTORY'"
            :name="name"
            :description="description"
            :required="required"
            :value="getSelectionFieldValue(index, key)"
            @input="value => setSelectionFieldValue(index, key, value)"
          />
          <location-search
            v-if="type === 'LOCATION'"
            :value="getSelectionFieldValue(index, key) ? getSelectionFieldValue(index, key).location : null"
            @input="value => setSelectionFieldValue(index, key, {
              remote: getSelectionFieldValue(index, key) ? getSelectionFieldValue(index, key).remote : null,
              location: value,
            })"
          />
          <b-form-checkbox
              v-if="type === 'LOCATION'"
              :checked="getSelectionFieldValue(index, key) ? getSelectionFieldValue(index, key).remote : false"
              :value="getSelectionFieldValue(index, key) ? getSelectionFieldValue(index, key).remote : false"
              @input="value => setSelectionFieldValue(index, key, {
                remote: value,
                location: getSelectionFieldValue(index, key) ? getSelectionFieldValue(index, key).location : null,
              })"
              size="lg"
          ><span class="pt-2"> Are you interested in opportunities with remote working?</span></b-form-checkbox>
        </div>
      </b-card>
    </b-col>

    <b-col cols="12" class="d-flex justify-content-between">
      <b-button variant="link-primary" @click="$emit('cancel')">Cancel</b-button>
      <b-button variant="primary" @click="$emit('input', { inputValues, skillSuggestions })">Save</b-button>
    </b-col>
  </b-row>
</template>

<script>
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import JobTitleSelector from '@/components/profile/JobTitleSelector';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import LanguagesSelector from '@/components/profile/LanguagesSelector';
import QualificationsEditor from '@/components/profile/QualificationsEditor';
import DateEditor from '@/components/profile/DateEditor';
import JobHistoryEditor from '@/components/profile/JobHistoryEditor';
import LocationSearch from '@/components/misc/LocationSearch';
import JobBusinessAreaSelector from '@/components/profile/JobBusinessAreaSelector';
import JobSenioritySelector from '@/components/profile/JobSenioritySelector';
import AssetBlock from '@/components/settings/AssetBlock';

export default {
  name: 'ProfileEditor',
  components: {
    OnboardingStepper,
    JobTitleSelector,
    JobSkillsSelector,
    LanguagesSelector,
    QualificationsEditor,
    DateEditor,
    JobHistoryEditor,
    LocationSearch,
    JobBusinessAreaSelector,
    JobSenioritySelector,
    AssetBlock,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    inputSelectionField: {
      type: String,
      default: () => null,
    },
    value: {
      type: Object,
      default: () => ({}),
    }
  },
  data() {
    return {
      inputValues: this.value,
      skillSuggestions: {
        JOB_SKILLS: [],
        JOB_DEVELOPMENT_SKILLS: []
      },
    };
  },
  methods: {
    getSelectionFieldValue(index, key) {
      const { inputValues, inputSelectionField } = this;
      console.log({ index, key });
      console.log(inputValues[inputSelectionField][index]);
      if (Array.isArray(inputValues[inputSelectionField]) && Array.isArray(inputValues[inputSelectionField][index])) {
        const valueIndex = inputValues[inputSelectionField][index].findIndex(el => el.key === key);
        return inputValues[inputSelectionField][index][valueIndex] ? inputValues[inputSelectionField][index][valueIndex].value : null;
      }
    },
    setSelectionFieldValue(index, key, value) {
      const { inputValues, inputSelectionField } = this;
      if (Array.isArray(inputValues[inputSelectionField]) && Array.isArray(inputValues[inputSelectionField][index])) {
        const valueIndex = inputValues[inputSelectionField][index].findIndex(el => el.key === key);
        if (valueIndex !== -1) {
          this.$set(this.inputValues[inputSelectionField][index], valueIndex, { key, value });
        } else {
          this.inputValues[inputSelectionField][index].push({ key, value });
        }
      } else {
        if (Array.isArray(inputValues[inputSelectionField])) {
          this.inputValues[inputSelectionField].push([ { key, value } ]);
        } else {
          this.inputValues[inputSelectionField] = [ [ { key, value } ] ];
        }
      }
    },
    setJobCategory(input) {
      console.log('EMITTED 1: ', input);
      const { fields } = this;
      const field = (Array.isArray(fields) ? fields : [])
        .find(field => field.key.startsWith('HIDDEN') && field.type === 'JOB_BUSINESS_AREA');
      if (input && field && field.key) {
        this.inputValues[field.key] = input;
      };
      console.log('INPUT VALUES: ', this.inputValues);
    },
    skillsInput(input, key, type) {
      if (this.$root.isCXR) {
        this.inputValues[key] = input;
      } else {
        this.inputValues[key] = input.input;
        this.skillSuggestions[type] = input.newTerms;
        console.log('NEW SKILL SUGGESTIONS FROM INPUT: ', this.skillSuggestions);
      }
    }
  },
  watch: {
    value() {
      this.inputValues = this.value;
      console.log('INPUT VALUES: ', this.inputValues);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/profile/ProfileEditor.vue